import { gql, useMutation } from '@apollo/client';
import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogTitle,
  Avatar,
  Button,
  ButtonGroup,
  FormErrorMessage,
} from '@apollo/orbit';
import React, { useCallback, useRef, useState } from 'react';

import { proposalRouteConfig } from 'src/app/graph/routes';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { assertUnreachableOrReturnDefault } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import {
  Maybe,
  RequestedReviewersSectionServiceFragment_defaultProposalReviewers,
} from 'src/lib/graphqlTypes/types';

import { ProposalUpdateRequestedReviewersMutationGQL } from '../RequestedReviewersSection';

export const ReplaceRequestedReviewersModal = ({
  proposalId,
  isOpen,
  onClose,
  defaultReviewers,
}: {
  proposalId: string;
  isOpen: boolean;
  onClose: () => void;
  defaultReviewers: Array<
    Maybe<RequestedReviewersSectionServiceFragment_defaultProposalReviewers>
  >;
}) => {
  const [errorMessage, setErrorMessage] = useState('');
  const cancelRef = useRef<HTMLButtonElement>(null);
  const { graphId, graphVariant } = useRouteParams(proposalRouteConfig);

  const [updateReviewers, { loading: updateReviewersLoading }] = useMutation<
    GraphQLTypes.ProposalUpdateRequestedReviewersMutation,
    GraphQLTypes.ProposalUpdateRequestedReviewersMutationVariables
  >(ProposalUpdateRequestedReviewersMutationGQL);

  const [
    replaceRequestedReviewers,
    { loading: replaceRequestedReviewersLoading },
  ] = useMutation<
    GraphQLTypes.ReplaceReviewersWithDefaultReviewersMutation,
    GraphQLTypes.ReplaceReviewersWithDefaultReviewersMutationVariables
  >(gql`
    mutation ReplaceReviewersWithDefaultReviewersMutation(
      $graphId: ID!
      $graphVariant: String!
    ) {
      graph(id: $graphId) {
        variant(name: $graphVariant) {
          proposal {
            ... on NotFoundError {
              message
            }
            ... on ValidationError {
              message
            }
            ... on PermissionError {
              message
            }
            ... on ProposalMutation {
              replaceReviewersWithDefaultReviewers {
                ... on ValidationError {
                  message
                }
                ... on PermissionError {
                  message
                }
                ... on Proposal {
                  id
                  requestedReviewers {
                    user {
                      id
                      ... on User {
                        fullName
                        avatarUrl
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `);

  const handleReplace = useCallback(async () => {
    const { data: responseData } = await replaceRequestedReviewers({
      variables: {
        graphId,
        graphVariant,
      },
      update: (cache) => {
        cache.evict({
          id: cache.identify({
            __typename: 'Proposal',
            id: proposalId,
          }),
          fieldName: 'defaultReviewers',
        });
      },
    });

    const responseProposal = responseData?.graph?.variant?.proposal;

    if (
      responseProposal?.__typename === 'ProposalMutation' &&
      responseProposal?.replaceReviewersWithDefaultReviewers.__typename ===
        'Proposal'
    ) {
      onClose();
      setErrorMessage('');
    } else if (
      responseProposal?.__typename === 'ValidationError' ||
      responseProposal?.__typename === 'NotFoundError' ||
      responseProposal?.__typename === 'PermissionError'
    ) {
      const error = responseProposal?.message;
      setErrorMessage(`An error has occurred ${error}`);
    } else if (
      responseProposal?.replaceReviewersWithDefaultReviewers.__typename ===
        'PermissionError' ||
      responseProposal?.replaceReviewersWithDefaultReviewers.__typename ===
        'ValidationError'
    ) {
      const error =
        responseProposal?.replaceReviewersWithDefaultReviewers.message;
      setErrorMessage(`An error has occurred ${error}`);
    } else if (!responseProposal?.replaceReviewersWithDefaultReviewers) {
      setErrorMessage('An error has occurred');
    } else if (
      responseProposal.replaceReviewersWithDefaultReviewers.__typename !==
      'Proposal'
    ) {
      setErrorMessage(
        assertUnreachableOrReturnDefault(
          responseProposal.replaceReviewersWithDefaultReviewers,
          'An error has occurred',
        ),
      );
    }
  }, [graphId, graphVariant, onClose, proposalId, replaceRequestedReviewers]);

  const handleUpdateReviewers = useCallback(async () => {
    const { data: responseData } = await updateReviewers({
      variables: {
        graphId,
        graphVariant,
        input: {
          reviewerUserIdsToAdd: defaultReviewers
            .filter((reviewer) => reviewer?.id !== undefined)
            .map((reviewer) => reviewer?.id ?? ''),
          reviewerUserIdsToRemove: null,
        },
      },
    });

    const responseProposal = responseData?.graph?.variant?.proposal;

    if (
      responseProposal?.__typename === 'ProposalMutation' &&
      responseProposal?.updateRequestedReviewers.__typename === 'Proposal'
    ) {
      onClose();
      setErrorMessage('');
    } else if (
      responseProposal?.__typename === 'ValidationError' ||
      responseProposal?.__typename === 'NotFoundError' ||
      responseProposal?.__typename === 'PermissionError'
    ) {
      const error = responseProposal?.message;
      setErrorMessage(`An error has occurred ${error}`);
    } else if (
      (responseProposal?.__typename === 'ProposalMutation' &&
        responseProposal.updateRequestedReviewers.__typename ===
          'PermissionError') ||
      responseProposal?.updateRequestedReviewers.__typename ===
        'ValidationError'
    ) {
      const error = responseProposal?.updateRequestedReviewers.message;
      setErrorMessage(`An error has occurred ${error}`);
    }
  }, [updateReviewers, graphId, graphVariant, defaultReviewers, onClose]);

  return (
    <AlertDialog
      size="xl"
      isOpen={isOpen}
      onClose={onClose}
      leastDestructiveRef={cancelRef}
    >
      <AlertDialogOverlay />
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Default Reviewers</AlertDialogTitle>
        </AlertDialogHeader>
        <AlertDialogBody>
          <p>
            A new list of default reviewers has been assigned to this graph. How
            would you like to proceed?
          </p>
          <p>
            You’ll still be able to include additional reviewers in your
            proposals.
          </p>
          <Alert status="warning">
            Selecting “Replace all” will remove reviews from previous default
            reviewers
          </Alert>
          {!!errorMessage && (
            <FormErrorMessage>{errorMessage}</FormErrorMessage>
          )}
          <div className="space-y-4 rounded-md border border-primary p-4">
            {defaultReviewers.map((reviewer) => {
              return reviewer?.__typename === 'User' ? (
                <div key={reviewer.id} className="flex items-center">
                  <Avatar
                    size="sm"
                    name={reviewer.fullName}
                    src={reviewer.avatarUrl ?? undefined}
                    className="mr-2"
                  />
                  <span className="truncate">{reviewer.fullName}</span>
                </div>
              ) : (
                <div key={reviewer?.id} className="flex items-center">
                  <Avatar size="sm" className="mr-2" />
                  <div className="flex flex-col">Unknown</div>
                </div>
              );
            })}
          </div>
        </AlertDialogBody>
        <AlertDialogFooter>
          <ButtonGroup>
            <Button
              variant="secondary"
              isLoading={updateReviewersLoading}
              loadingText="Updating"
              onClick={handleUpdateReviewers}
              type="button"
              ref={cancelRef}
            >
              Update, but keep previous reviewers
            </Button>
            <Button
              variant="destructive"
              isLoading={replaceRequestedReviewersLoading}
              loadingText="Replacing"
              onClick={handleReplace}
              type="submit"
            >
              Replace all
            </Button>
          </ButtonGroup>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
